import apiClient from "#/redux/api/ApiClient";

function handleMutationResponse({ data }) {
  return data.output;
}

function normalizeApiError(err) {
  const { status, data } = err.response || {};
  const error = {
    ...data,
    status,
    message: data?.resultMessages || err.message,
    response: err.response,
  };

  throw error;
}
/**
 * Generic POKK service API interface
 *
 * This class provides a generic interface for performing CRUD operations on a POKK resource.
 * It abstracts away the details of the API endpoints and allows for easy integration with different resources by simply providing a base URL.
 *
 * The class includes methods for querying, reading, creating, updating, and deleting resources, all of which interact with the provided API client.
 *
 * @param {object} param
 * @param {string} param.baseUrl - The base URL for a specific POKK resource, which will be used to construct the API endpoints for CRUD operations.
 * @param {object} param.client - An instance of an API client to handle HTTP requests. Defaults to a generic API client if not provided.
 *
 * @returns {object} An instance of the KeyValuePokkService class with methods for interacting with the specified POKK resource.
 *
 * @example
 * const horseService = new KeyValuePokkService({ baseUrl: "horses" });
 */
export default class KeyValuePokkService {
  baseUrl = undefined;

  /**
   * @var {ApiClient} client - An instance of an API client to handle HTTP requests.
   */
  client = undefined;

  constructor({ client = apiClient, ...params } = {}) {
    this.client = client;
    for (const [key, value] of Object.entries(params)) {
      if (key in this) {
        this[key] = value;
      }
    }
  }

  /**
   * Constructs the API endpoint URL based on the baseUrl and any additional parts provided.
   *
   * @param  {...any} parts
   * @returns string - The constructed API endpoint URL.
   */
  #endpoint(key) {
    return [this.baseUrl, "key", key].join("/");
  }

  async get(key) {
    return this.client
      .get(this.#endpoint(key))
      .then(({ data }) => data)
      .catch(normalizeApiError);
  }

  async put(key, value) {
    return this.client
      .put(this.#endpoint(key), value)
      .then(handleMutationResponse)
      .catch(normalizeApiError);
  }
}
